<script setup>
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  watch,
  nextTick,
} from 'vue';
import { useBlockSafe } from '@/composables/useBlockSafe';
import { useRequest } from '@/composables/useRequest';
import FormServicesCalculator from '@/components/forms/FormServicesCalculator.vue';
const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
});
const {
  id,
  backgroundColor,
  spacingTop,
  spacingBottom,
  title,
  hasTitle,
  description,
  hasDescription,
  hasAnyContent,
} = useBlockSafe(props.data);

const selectBackgroundColor = computed(() => {
  const colorMap = {
    white: 'gray',
    gray: 'white',
    black: 'black',
  };

  return colorMap[backgroundColor.value] || backgroundColor.value;
});

const searchQuery = ref('');

const filteredGroups = computed(() => {
  const query = searchQuery.value.toLowerCase().trim();

  if (!query || query.length < 3) return props.data.services.groups;

  return (props.data.services.groups || [])
    .map((group) => ({
      ...group,
      items: group.items.filter((item) =>
        item.name.toLowerCase().includes(query),
      ),
    }))
    .filter((group) => group.items.length > 0);
});

const clearSearchQuery = () => {
  searchQuery.value = '';
};
const selectedServices = ref([]);
const totalPrice = ref(0);

const calculateTotal = () => {
  const grouped = selectedServices.value.reduce((acc, item) => {
    const currency = item.price.currency;

    if (!acc[currency]) {
      acc[currency] = 0;
    }

    if (item.price.from && !item.price.and) {
      acc[currency] += item.price.from;
    }
    if (item.price.from && item.price.and) {
      acc[currency] += item.price.from + item.price.and;
    }

    return acc;
  }, {});
  totalPrice.value = Object.entries(grouped)
    .map(
      ([currency, sum]) =>
        $t('service_calculator.price_from') +
        ` ${formatPrice(sum)} ${currency}`,
    )
    .join(' + ');
};

const formatPrice = (price) => {
  return price.toLocaleString('uk-UA');
};

const isAtBottom = ref(false);

const scrollPanel = ref(null);
const handleScroll = (e) => {
  const el = e.target;
  isAtBottom.value = el.scrollTop + el.clientHeight >= el.scrollHeight - 4;
};

onMounted(() => {
  const scrollableDiv = scrollPanel.value.$el.querySelector(
    '.p-scrollpanel-content',
  );
  scrollableDiv.addEventListener('scroll', handleScroll);
});

onBeforeUnmount(() => {
  const scrollableDiv = scrollPanel.value.$el.querySelector(
    '.p-scrollpanel-content',
  );
  scrollableDiv.removeEventListener('scroll', handleScroll);
});
</script>
<template>
  <section
    v-if="hasAnyContent"
    :id="id"
    class="section"
    :class="[
      backgroundColor ? `bg-${backgroundColor}` : '',
      backgroundColor === 'black' ? 'white' : 'black',
      spacingTop ? `section--top-${spacingTop}` : 'section--top-xl',
      spacingBottom ? `section--bottom-${spacingBottom}` : 'section--bottom-xl',
    ]"
    data-aos="fade-up"
    data-aos-easing="ease-out-cubic"
  >
    <div class="container">
      <div class="service-calculator">
        <div class="service-calculator__top text-center">
          <h2
            v-if="hasTitle"
            class="mb-0 service-calculator__title h2"
            data-aos="fade-up"
            data-aos-easing="ease-out-cubic"
            v-html="title"
          ></h2>
          <p
            v-if="hasDescription"
            class="mb-0 service-calculator__description body-1-regular"
            data-aos="fade-up"
            data-aos-easing="ease-out-cubic"
            v-html="description"
          ></p>
        </div>
        <div
          :class="[
            `service-calculator__wrap service-calculator__wrap--${backgroundColor}`,
          ]"
          data-aos="fade-up"
          data-aos-easing="ease-out-cubic"
        >
          <form class="service-calculator__form">
            <p
              v-if="data?.services?.title"
              class="m-0 h4 service-calculator__form-title"
              v-html="data?.services?.title"
            ></p>
            <div class="service-calculator__search">
              <InputGroup
                class="service-calculator__search-field"
                :class="[
                  searchQuery
                    ? 'service-calculator__search-field--with-value'
                    : '',
                ]"
              >
                <InputGroupAddon class="service-calculator__search-icon">
                  <CIcon
                    name="search-black"
                    width="16"
                    height="16"
                    class="service-calculator__search-icon-el"
                  />
                </InputGroupAddon>

                <InputText
                  v-model="searchQuery"
                  type="text"
                  size="small"
                  :placeholder="$t('service_calculator.field_placeholder')"
                  enter-key-hint="search"
                  inputmode="search"
                  class="service-calculator__search-input search-input"
                />

                <InputGroupAddon
                  v-if="searchQuery"
                  class="service-calculator__search-button"
                  @click="clearSearchQuery"
                >
                  <CIcon
                    class="service-calculator__search-button-icon"
                    name="close-black"
                    width="16"
                    height="16"
                  />
                </InputGroupAddon>
              </InputGroup>

              <p
                v-if="filteredGroups?.length === 0"
                class="mb-0 service-calculator__search-error body-3-medium"
              >
                {{ $t('service_calculator.services_empty') }}
              </p>
            </div>
            <div
              v-if="filteredGroups?.length > 0"
              class="service-calculator__groups"
            >
              <ScrollPanel
                ref="scrollPanel"
                style="width: 100%"
                class="service-calculator__groups-panel"
                :pt="{
                  wrapper: { style: 'border-right: 2px solid #e0e0e0;' },
                  bary: { style: 'opacity: 1; background: #000; width: 4px;' },
                }"
              >
                <div
                  v-for="group in filteredGroups"
                  :key="group.id"
                  class="service-calculator__group"
                >
                  <p
                    v-if="group.title"
                    :class="[
                      `m-0 body-2-bold service-calculator__group-title service-calculator__group-title--${backgroundColor}`,
                    ]"
                    v-html="group?.title"
                  ></p>
                  <div
                    v-if="group.items && group.items.length > 0"
                    class="service-calculator__group-items"
                  >
                    <div
                      v-for="item in group.items"
                      :key="item.id"
                      class="service-calculator__group-item"
                    >
                      <Checkbox
                        v-model="selectedServices"
                        :input-id="item.id"
                        :value="item"
                        class="service-calculator__group-item-checkbox checkbox-input"
                        @change="calculateTotal"
                      >
                        <template #icon>
                          <CIcon
                            name="check-white"
                            width="12"
                            height="12"
                            class="checkbox-icon"
                          />
                        </template>
                      </Checkbox>
                      <label
                        :for="item.id"
                        class="service-calculator__group-item-label"
                      >
                        <span
                          class="service-calculator__group-item-name checkbox-label body-3-regular"
                          >{{ item.name }}</span
                        >
                        <span
                          class="service-calculator__group-item-price body-3-2-bold"
                        >
                          <template v-if="item.price.from && !item.price.and">
                            {{ $t('service_calculator.price_from') }}
                            {{ formatPrice(item.price.from) }}
                            {{ item.price.currency }}
                          </template>
                          <template v-if="item.price.from && item.price.and">
                            {{ $t('service_calculator.price_from') }}
                            {{ formatPrice(item.price.from) }}
                            {{ $t('service_calculator.price_and') }}
                            {{ formatPrice(item.price.and) }}
                            {{ item.price.currency }}
                          </template>
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              </ScrollPanel>
              <div
                class="service-calculator__groups-panel-fade"
                :class="{ hidden: isAtBottom }"
              />
            </div>
          </form>
          <div
            :class="[
              'service-calculator__results',
              totalPrice ? 'service-calculator__results--result' : '',
            ]"
          >
            <p class="h4 m-0 service-calculator__results-title">
              {{ $t('service_calculator.result_title') }}
            </p>
            <template v-if="totalPrice">
              <div class="service-calculator__media">
                <NuxtImg
                  src="/images/style/service-calculator-image.png"
                  alt="calculator car"
                  height="300"
                  width="320"
                  class="service-calculator__image"
                  loading="lazy"
                />
              </div>
              <div class="service-calculator__results-price">
                <span class="body-3-regular">
                  <template v-if="selectedServices.length === 1">
                    {{
                      $t('service_calculator.one_service', {
                        count: selectedServices.length,
                      })
                    }}
                  </template>
                  <template
                    v-else-if="
                      selectedServices.length > 1 &&
                      selectedServices.length <= 4
                    "
                  >
                    {{
                      $t('service_calculator.few_service', {
                        count: selectedServices.length,
                      })
                    }}
                  </template>
                  <template v-else>
                    {{
                      $t('service_calculator.many_service', {
                        count: selectedServices.length,
                      })
                    }}
                  </template>
                </span>
                <span class="h6-mobile">
                  {{ totalPrice }}
                </span>
              </div>
              <div class="service-calculator__results-form">
                <FormServicesCalculator :services="selectedServices" />
              </div>
            </template>
            <template v-else>
              <div class="service-calculator__stub">
                <div class="service-calculator__media">
                  <NuxtImg
                    src="/images/style/service-calculator-image.png"
                    alt="calculator car"
                    height="300"
                    width="320"
                    class="service-calculator__image"
                    loading="lazy"
                  />
                </div>
                <p
                  class="m-0 service-calculator__placeholder body-1-regular text-center"
                >
                  {{ $t('service_calculator.enter_parameters') }}
                </p>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
@use 'sass:math';
.service-calculator {
  display: flex;
  flex-direction: column;
  gap: 56px;
  @include media-breakpoint-up(sm) {
    gap: 80px;
  }
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(gap, 80);
  }
  &__top {
    display: grid;
    gap: 32px;
    justify-items: center;
    @include media-breakpoint-up(sm) {
      gap: 48px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 48);
    }
  }
  &__description {
    @include media-breakpoint-up(sm) {
      max-width: 584px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(max-width, 584);
    }
  }
  &__stub {
    display: grid;
    grid-template-rows: min-content min-content;
    gap: 8px;
    @include media-breakpoint-up(sm) {
      gap: 40px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 40);
    }
  }
  &__media {
    display: grid;
    grid-template-rows: min-content;
    justify-content: center;
  }
  &__image {
    height: auto;
    max-width: 100%;
    @include media-breakpoint-up(sm) {
      height: auto;
      max-width: 285px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(max-width, 285);
    }
  }
  &__placeholder {
    margin: 0 auto;
    @include media-breakpoint-up(sm) {
      max-width: 414px;
    }
    @include media-breakpoint-up(md) {
      max-width: unset;
    }
  }
  &__wrap {
    border-radius: 8px;
    @include media-breakpoint-up(md) {
      display: grid;
      grid-template-rows: min-content;
      grid-template-columns: repeat(2, 1fr);
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-radius, 8);
    }
    &--white {
      background-color: $gray;
    }
    &--black {
      background-color: rgba($surface-container-opacity, 0.5);
    }
    &--gray {
      background-color: $white;
    }
  }
  &__form {
    display: grid;
    grid-template-rows: min-content;
    gap: 24px;
    padding: 40px 16px 16px;
    @include media-breakpoint-up(sm) {
      gap: 40px;
      padding: 64px 48px 16px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(padding-top, 64);
      @include responsive-spacing(padding-bottom, 16);
      @include responsive-spacing(padding-left, 48);
      @include responsive-spacing(padding-right, 48);
      @include responsive-spacing(gap, 40);
    }
    &-title {
      text-align: center;
    }
  }
  &__results {
    border-top: 1px solid $divider;
    display: grid;
    gap: 32px;
    grid-template-rows: min-content;
    padding: 32px 16px;
    @include media-breakpoint-up(sm) {
      gap: 64px;
      padding: 64px;
    }
    @include media-breakpoint-up(md) {
      border-top: unset;
      border-left: 1px solid $divider;
      gap: 120px;
      padding: 64px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-top-width, 1);
      @include responsive-spacing(padding, 64);
      @include responsive-spacing(gap, 120);
    }
    &--result {
      gap: 24px;
      padding: 40px 16px;
      @include media-breakpoint-up(sm) {
        gap: 40px;
        padding: 64px 48px 62px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 40);
        @include responsive-spacing(padding-top, 64);
        @include responsive-spacing(padding-bottom, 62);
        @include responsive-spacing(padding-right, 48);
        @include responsive-spacing(padding-right, 48);
      }
    }
    &-title {
      text-align: center;
    }
    &-price {
      border-bottom: 1px solid $divider;
      display: grid;
      gap: 8px;
      justify-content: center;
      padding: 0 0 16px 0;
      @include media-breakpoint-up(sm) {
        align-items: center;
        grid-template-columns: auto auto;
        justify-content: space-between;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(border-top-width, 1);
        @include responsive-spacing(padding-bottom, 16);
        @include responsive-spacing(gap, 8);
      }
    }
  }
  &__search {
    position: relative;
    $parent: &;
    &-field {
      align-items: center;
      display: grid;
      grid-template-columns: 16px auto 16px;
      gap: 12px;
      border-bottom: 1px solid $black;
      grid-template-areas: 'search-icon search-input search-input';
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(border-bottom-width, 1);
        @include responsive-spacing(gap, 12);
        grid-template-columns: px-to-vw(16) auto px-to-vw(16);
      }
      &--with-value {
        grid-template-areas: 'search-icon search-input search-clear';
        #{$parent} {
          &-icon {
            display: none;
          }
        }
      }

      @include media-breakpoint-up(sm) {
        &--with-value {
          #{$parent} {
            &-icon {
              display: block;
            }
          }
        }
      }
    }
    &-icon {
      border: unset;
      grid-area: search-icon;
      margin: 0;
      min-width: unset;
      padding: 0;
      position: relative;
      &-el {
        height: 100%;
        width: 100%;
      }
    }
    &-input {
      border-end-end-radius: 0;
      border-start-end-radius: 0;
      border-radius: 0;
      grid-area: search-input;
      padding: 13px 0 14px !important;
      width: 100%;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(padding-bottom, 14, true);
        @include responsive-spacing(padding-top, 13, true);
      }
    }
    &-button {
      grid-area: search-clear;
      margin: 0;
      min-width: unset;
      padding: 0;
      position: relative;
      &-icon {
        height: 100%;
        width: 100%;
      }
    }
    &-error {
      background-color: $white;
      border-radius: 4px;
      box-shadow: 0px 4px 36px 0px #00000026;
      left: 0;
      position: absolute;
      padding: 16px;
      width: 100%;
      z-index: 1;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(padding, 16);
        @include responsive-spacing(border-radius, 4);
      }
    }
  }
  &__groups {
    position: relative;
    width: 100%;
    &-panel {
      max-height: 476px;
      @include media-breakpoint-up(sm) {
        max-height: 416px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(max-height, 416);
      }
      &:deep(.p-scrollpanel-content) {
        width: calc(100% - 6px);
        @include media-breakpoint-up(xl) {
          width: calc(100% - px-to-vw(6));
        }
      }
      &:deep(.p-scrollpanel-bar) {
        background-color: $on-surface-variant-2;
      }
      &:deep(.p-scrollpanel-bar-y) {
        opacity: 1 !important;
        visibility: visible !important;
        width: 2px;
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(width, 2);
        }
      }
      &:deep(.p-scrollpanel) {
        position: relative;
      }
      &:deep(.p-scrollpanel:after) {
        background-color: $divider;
        content: '';
        display: block;
        height: 100%;
        position: absolute;
        right: 2px;
        top: 0;
        width: 2px;
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(right, 2);
          @include responsive-spacing(width, 2);
        }
      }
      &-fade {
        background: linear-gradient(
          180deg,
          rgba(255, 255, 255, 0) 0%,
          #ffffff 95%
        );
        bottom: 0;
        left: 0;
        right: 0;
        height: 72px;
        pointer-events: none;
        position: absolute;
        z-index: 1;
        &.hidden {
          z-index: 0;
        }
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(height, 72);
        }
      }
    }
  }
  &__group {
    border-top: 1px solid $divider;
    display: grid;
    gap: 16px;
    padding: 16px 0;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-top-width, 1);
      @include responsive-spacing(gap, 16);
      @include responsive-spacing(padding-top, 16);
      @include responsive-spacing(padding-bottom, 16);
    }
    &:first-child {
      border-top: unset;
      padding-top: 0;
    }
    &:last-child {
      padding-bottom: 0;
    }

    &-title {
      position: sticky;
      top: 0;
      z-index: 1;
      &--white {
        background-color: $gray;
      }
      &--black {
        background-color: rgba($surface-container-opacity, 0.5);
      }
      &--gray {
        background-color: $white;
      }
    }
    &-items {
      display: grid;
      gap: 8px;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 8);
      }
    }
    &-item {
      align-items: start;
      display: grid;
      grid-template-columns: 24px auto;
      gap: 8px;
      padding: 4px 0;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(padding-top, 4);
        @include responsive-spacing(padding-bottom, 4);
        @include responsive-spacing(gap, 8);
        grid-template-columns: px-to-vw(24) auto;
      }
      &-checkbox {
        margin: 4px auto;
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(margin-top, 4);
          @include responsive-spacing(margin-bottom, 4);
        }
      }
      &-label {
        display: grid;
        padding-top: 2px;
        @include media-breakpoint-up(sm) {
          gap: 24px;
          grid-template-columns: auto auto;
          justify-content: space-between;
        }
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(gap, 24);
          @include responsive-spacing(padding-top, 2);
        }
      }
    }
  }
}
</style>
